// src/components/PlaceOrder/PlaceOrderScreen.js
import {
  FileEdit,
  Minus,
  Plus,
  ShoppingBag
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card, SearchBar, SuccessOverlay } from '../ui';
import {
  getCurrentShiftForSite,
  getDishesBySite,
  getTablesBySite,
  placeOrder,
} from './placeOrderApi';

// Utility function to combine Tailwind classes (same pattern as PatrollingDashboard)
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Card/panel styling (aligned with ApprovalWorkflow)
const panelStyle = {
  background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
  boxShadow: '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  backdropFilter: 'blur(30px)',
};

const INPUT_BASE_CLASS = 'w-full rounded-[10px] bg-white/80 border border-gray-200 px-3 py-2 text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400';

// Mock product data – replace with API when available
const MOCK_PRODUCTS = [
  { id: '1', name: 'Shrimp Basil Salad', category: 'Salads', price: 10, image: 'https://placehold.co/120x120/e8f5e9/2e7d32?text=Salad' },
  { id: '2', name: 'Onion Rings', category: 'Sides', price: 8, image: 'https://placehold.co/120x120/fff3e0/e65100?text=Rings' },
  { id: '3', name: 'Chicken Burger', category: 'Burgers', price: 12, image: 'https://placehold.co/120x120/ffebee/c62828?text=Burger' },
  { id: '4', name: 'Fresh Basil Salad', category: 'Salads', price: 10, image: 'https://placehold.co/120x120/e8f5e9/2e7d32?text=Basil' },
  { id: '5', name: 'Vegetable Rice', category: 'Rice', price: 9, image: 'https://placehold.co/120x120/fff8e1/f9a825?text=Rice' },
  { id: '6', name: 'Iced Coffee', category: 'Beverages', price: 5, image: 'https://placehold.co/120x120/efebe9/5d4037?text=Coffee' },
  { id: '7', name: 'Tomato Soup', category: 'Soup', price: 7, image: 'https://placehold.co/120x120/ffebee/d32f2f?text=Soup' },
  { id: '8', name: 'Margherita Pizza', category: 'Pizza', price: 14, image: 'https://placehold.co/120x120/e3f2fd/1976d2?text=Pizza' },
  { id: '9', name: 'Caesar Salad', category: 'Salads', price: 11, image: 'https://placehold.co/120x120/e8f5e9/2e7d32?text=Caesar' },
  { id: '10', name: 'Lemonade', category: 'Beverages', price: 4, image: 'https://placehold.co/120x120/fffde7/fbc02d?text=Lemon' },
];

const CATEGORIES = ['Show All', 'Rice', 'Beverages', 'Salads', 'Soup', 'Pizza', 'Burgers', 'Sides'];

const PlaceOrderScreen = () => {
  // Log screen view
  useScreenLogging('PlaceOrderScreen', {
    component: 'PlaceOrderScreen',
  });
  // Site and shift state
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState(null);
  const [ssId, setSsId] = useState(null);
  const [isLoadingShift, setIsLoadingShift] = useState(false);
  const [shiftError, setShiftError] = useState(null);

  // Product panel state
  const [productSearch, setProductSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Show All');
  const [dishes, setDishes] = useState([]);
  const [isDishesLoading, setIsDishesLoading] = useState(false);
  const [dishesError, setDishesError] = useState(null);

  // Tables for selected site (from GET /tables/tables)
  const [siteTables, setSiteTables] = useState([]);
  const [isLoadingTables, setIsLoadingTables] = useState(false);

  // Order panel state
  const [existingSearch, setExistingSearch] = useState('');
  const [diningOption, setDiningOption] = useState('Select Dining');
  const [tableOption, setTableOption] = useState('Select Table');
  const [orderNumber, setOrderNumber] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [productDiscount, setProductDiscount] = useState(240);
  const [extraDiscount, setExtraDiscount] = useState(0);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [editingNotesForItemId, setEditingNotesForItemId] = useState(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [lastCreatedOrderId, setLastCreatedOrderId] = useState(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [placeOrderError, setPlaceOrderError] = useState(null);

  // Responsive (same pattern as PatrollingDashboard)
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError(null);
      try {
        const response = await getAllSites();
        const sitesList = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
          ? response
          : [];
        
        // Sort sites alphabetically by name
        const sortedSites = [...sitesList].sort((a, b) => {
          const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
          const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setSites(sortedSites);
        // Auto-select first site in alphabetical order as default
        if (sortedSites.length > 0 && !selectedSite) {
          setSelectedSite(sortedSites[0]);
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
        setSitesError(error.message || 'Failed to fetch sites');
      } finally {
        setIsSitesLoading(false);
      }
    };
    fetchSites();
  }, []);

  // Fetch current shift when site is selected
  useEffect(() => {
    const fetchShift = async () => {
      if (!selectedSite) {
        setSsId(null);
        setShiftError(null);
        return;
      }

      const siteId = selectedSite.site_id || selectedSite.id;
      if (!siteId) {
        console.error('Site ID not found');
        setSsId(null);
        setShiftError('Invalid site selected');
        return;
      }

      setIsLoadingShift(true);
      setShiftError(null);
      try {
        const shiftData = await getCurrentShiftForSite(siteId);
        
        // getCurrentShiftForSite returns null if no shift found (404)
        if (!shiftData) {
          setShiftError('No active shift found for this site today. Please ensure a shift is scheduled.');
          setSsId(null);
          return;
        }

        const shiftScheduleId = shiftData?.ss_id || shiftData?.shift_id || shiftData?.shift_schedule_id;
        if (shiftScheduleId) {
          setSsId(shiftScheduleId);
          setShiftError(null); // Clear any previous error
          
          // Log API event for fetching current shift
          loggingService.logInfo(
            'shift_current_fetch',
            `Fetched current shift for site ${siteId}`,
            {
              endpoint: '/shift/current-for-site',
              method: 'GET',
              siteId: siteId,
              shiftScheduleId: shiftScheduleId,
            }
          ).catch(logError => {
            console.warn('[PlaceOrderScreen] Failed to log event:', logError);
          });
        } else {
          setShiftError('No active shift found for this site today. Please ensure a shift is scheduled.');
          setSsId(null);
        }
      } catch (error) {
        console.error('Error fetching shift:', error);
        setShiftError(error.message || 'Failed to fetch shift information. Please try again.');
        setSsId(null);
      } finally {
        setIsLoadingShift(false);
      }
    };

    fetchShift();
  }, [selectedSite]);

  // Fetch tables when site is selected (GET /tables/tables?site_id=...)
  useEffect(() => {
    if (!selectedSite) {
      setSiteTables([]);
      setTableOption('Select Table');
      return;
    }
    const siteId = selectedSite.site_id || selectedSite.id;
    if (!siteId) return;
    let cancelled = false;
    setIsLoadingTables(true);
    getTablesBySite(siteId)
      .then((tables) => {
        if (!cancelled) {
          const tablesArray = Array.isArray(tables) ? tables : [];
          setSiteTables(tablesArray);
          setTableOption('Select Table');
          
          // Log API event for fetching tables
          loggingService.logInfo(
            'tables_fetch',
            `Fetched ${tablesArray.length} tables for site ${siteId}`,
            {
              endpoint: '/tables/tables',
              method: 'GET',
              siteId: siteId,
              tableCount: tablesArray.length,
            }
          ).catch(logError => {
            console.warn('[PlaceOrderScreen] Failed to log event:', logError);
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Failed to load tables:', err);
          setSiteTables([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTables(false);
      });
    return () => { cancelled = true; };
  }, [selectedSite]);

  // Fetch dishes when site is selected
  useEffect(() => {
    const fetchDishes = async () => {
      if (!selectedSite) {
        setDishes([]);
        return;
      }

      const siteId = selectedSite.site_id || selectedSite.id;
      if (!siteId) {
        console.error('Site ID not found');
        return;
      }

      setIsDishesLoading(true);
      setDishesError(null);
      try {
        const dishesData = await getDishesBySite(siteId);
        const dishesArray = Array.isArray(dishesData) ? dishesData : [];
        setDishes(dishesArray);
        
        // Log API event for fetching dishes
        loggingService.logInfo(
          'dishes_fetch',
          `Fetched ${dishesArray.length} dishes for site ${siteId}`,
          {
            endpoint: '/restaurant/dish/getall-by-site',
            method: 'POST',
            siteId: siteId,
            dishCount: dishesArray.length,
          }
        ).catch(logError => {
          console.warn('[PlaceOrderScreen] Failed to log event:', logError);
        });
      } catch (error) {
        console.error('Error fetching dishes:', error);
        setDishesError(error.message || 'Failed to fetch dishes');
        setDishes([]);
      } finally {
        setIsDishesLoading(false);
      }
    };

    fetchDishes();
  }, [selectedSite]);

  // Extract categories from dishes
  const categories = useMemo(() => {
    const categorySet = new Set(['Show All']);
    dishes.forEach((dish) => {
      // Try multiple possible field names for category
      const category = dish.category || dish.dish_category || dish.Category || dish.item_category;
      if (category) {
        categorySet.add(category);
      } else {
        // If no category, add "Uncategorized" so dishes without categories are still accessible
        categorySet.add('Uncategorized');
      }
    });
    return Array.from(categorySet);
  }, [dishes]);

  // Transform dishes to products format
  const products = useMemo(() => {
    const transformed = dishes.map((dish) => {
      // Try multiple possible field names for dish name
      const dishName = dish.dish_name || dish.name || dish.Name || dish.dishName || dish.item_name || 'Unnamed Dish';
      
      // Try multiple possible field names for category
      const dishCategory = dish.category || dish.dish_category || dish.Category || dish.item_category || 'Uncategorized';
      
      // Try multiple possible field names for price (API may return Price with capital P)
      const dishPrice = dish.Price !== undefined ? dish.Price : 
                       (dish.price !== undefined ? dish.price : 
                       (dish.dish_price !== undefined ? dish.dish_price : 
                       (dish.item_price !== undefined ? dish.item_price : 0)));
      
      return {
        id: dish.dish_id || dish.id || dish.item_id,
        name: dishName,
        category: dishCategory,
        price: typeof dishPrice === 'number' ? dishPrice : parseFloat(dishPrice) || 0,
        image: dish.image || dish.image_url || dish.Image || null,
      };
    });
    
    return transformed;
  }, [dishes]);

  // Table dropdown options from GET /tables/tables (value = table_id so backend validates correctly)
  const tableOptions = useMemo(() => {
    return siteTables
      .filter((t) => t.table_id != null && t.table_id !== '')
      .map((t) => {
        const no = t.table_no != null ? t.table_no : t.table_number;
        const label = t.table_label || (no != null ? `Table ${no}` : t.table_id || 'Table');
        return { value: String(t.table_id), label };
      });
  }, [siteTables]);

  // Filtered products by search and category (use API dishes when integrated)
  const filteredProducts = useMemo(() => {
    let list = products;
    if (selectedCategory !== 'Show All') {
      list = list.filter((p) => p.category === selectedCategory);
    }
    if (productSearch.trim()) {
      const term = productSearch.trim().toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(term));
    }
    return list;
  }, [products, productSearch, selectedCategory]);

  const addToOrder = useCallback((product) => {
    setOrderItems((prev) => {
      const existing = prev.find((i) => i.productId === product.id);
      if (existing) {
        return prev.map((i) =>
          i.productId === product.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [
        ...prev,
        { 
          id: `o${Date.now()}`, 
          productId: product.id, 
          dishId: product.id, // For API call
          name: product.name, 
          price: product.price, 
          quantity: 1, 
          notes: '' 
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((itemId, delta) => {
    setOrderItems((prev) =>
      prev.map((i) => {
        if (i.id !== itemId) return i;
        const qty = Math.max(0, i.quantity + delta);
        if (qty === 0) return null;
        return { ...i, quantity: qty };
      }).filter(Boolean)
    );
  }, []);

  const removeItem = useCallback((itemId) => {
    setOrderItems((prev) => prev.filter((i) => i.id !== itemId));
  }, []);

  const setItemNotes = useCallback((itemId, notes) => {
    setOrderItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, notes } : i))
    );
  }, []);

  // Filter current order items by "Search in Existing" (by item name)
  const filteredOrderItems = useMemo(() => {
    if (!existingSearch.trim()) return orderItems;
    const term = existingSearch.trim().toLowerCase();
    return orderItems.filter((i) => i.name.toLowerCase().includes(term));
  }, [orderItems, existingSearch]);

  // Order totals (always from full order, not filtered view)
  const subTotal = useMemo(
    () => orderItems.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [orderItems]
  );
  const total = useMemo(
    () => subTotal - productDiscount - extraDiscount - couponDiscount,
    [subTotal, productDiscount, extraDiscount, couponDiscount]
  );

  // Handle place order
  const handlePlaceOrder = useCallback(async () => {
    if (!selectedSite) {
      setPlaceOrderError('Please select a site');
      return;
    }

    if (!ssId) {
      setPlaceOrderError('No active shift found. Please ensure a shift is scheduled for this site.');
      return;
    }

    if (orderItems.length === 0) {
      setPlaceOrderError('Please add items to the order');
      return;
    }

    if (diningOption === 'Select Dining') {
      setPlaceOrderError('Please select a dining option');
      return;
    }

    if (!tableOption || tableOption === 'Select Table' || tableOption === '') {
      setPlaceOrderError('Please select a table');
      return;
    }

    const siteId = selectedSite.site_id || selectedSite.id;
    if (!siteId) {
      setPlaceOrderError('Invalid site selected');
      return;
    }

    setIsPlacingOrder(true);
    setPlaceOrderError(null);

    try {
      const orderData = orderItems.map((item) => {
        const orderItem = {
          dish_id: item.dishId || item.productId,
          qty: item.quantity,
        };
        // Send notes as both 'notes' and 'cust_mods' to ensure backend stores it correctly
        // Backend appears to store notes in 'cust_mods' field
        if (item.notes !== undefined && item.notes !== null) {
          const notesValue = String(item.notes).trim();
          if (notesValue) {
            orderItem.notes = notesValue;
            orderItem.cust_mods = notesValue; // Also send as cust_mods since backend uses this field
          }
        }
        return orderItem;
      });
      
      // Debug: Log order data being sent
      console.log('[Place Order] Sending order data with notes:', JSON.stringify(orderData, null, 2));

      // When we have tables from API, dropdown value is table_id; otherwise manual input is table_no
      const payload =
        tableOptions.length > 0
          ? { order_data: orderData, site_id: siteId, ss_id: ssId, table_id: tableOption }
          : (() => {
              const tableNumber = parseInt(tableOption, 10);
              if (isNaN(tableNumber) || tableNumber <= 0) {
                setPlaceOrderError('Invalid table number');
                return null;
              }
              return { order_data: orderData, site_id: siteId, ss_id: ssId, table_no: tableNumber };
            })();
      if (!payload) return;

      const orderResponse = await placeOrder(payload);

      // Success - log and show overlay with order ID
      const orderId = orderResponse?.order_id || orderResponse?.id || null;
      console.log('Order created successfully.', orderResponse);
      
      // Log API event for placing order
      loggingService.logInfo(
        'order_place',
        `Placed order ${orderId} for site ${siteId}`,
        {
          endpoint: '/restaurant/order/place',
          method: 'POST',
          siteId: siteId,
          shiftScheduleId: ssId,
          orderId: orderId,
          itemCount: orderData.length,
          tableId: payload.table_id || null,
          tableNo: payload.table_no || null,
        }
      ).catch(logError => {
        console.warn('[PlaceOrderScreen] Failed to log event:', logError);
      });
      
      setLastCreatedOrderId(orderId);
      setShowSuccessOverlay(true);
      setOrderItems([]);
      setPlaceOrderError(null);
    } catch (error) {
      console.error('Error placing order:', error);
      setPlaceOrderError(error.message || 'Failed to place order. Please try again.');
    } finally {
      setIsPlacingOrder(false);
    }
  }, [selectedSite, ssId, orderItems, diningOption, tableOption]);

  return (
    <div className="relative w-full h-full flex flex-col">
      {/* Main two-column layout (same structure as PatrollingDashboard: flex-1, gap-4, responsive) */}
      <div
        className={combineClasses(
          'flex flex-1 w-full overflow-hidden gap-4',
          isMobileViewport ? 'flex-col' : 'flex-row'
        )}
      >
        {/* Left: Product selection panel */}
        <Card
          variant="custom"
          className={combineClasses(
            'flex flex-col overflow-hidden flex-shrink-0',
            isMobileViewport ? 'w-full flex-1 min-h-[320px]' : 'flex-1 min-w-0'
          )}
          style={panelStyle}
        >
          {/* Site selection and filters row */}
          <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-100">
            <select
              value={selectedSite?.site_id || selectedSite?.id || ''}
              onChange={(e) => {
                const site = sites.find((s) => (s.site_id || s.id) === e.target.value);
                setSelectedSite(site || null);
              }}
              className="h-11 px-4 rounded-[10px] bg-white/80 border border-gray-200 text-gray-700 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400"
              disabled={isSitesLoading}
            >
              {/* <option value="">
                {isSitesLoading ? 'Loading sites...' : 'Select Site'}
              </option> */}
              {/* alphabetical order */}
              {Array.isArray(sites) && [...sites]
                .sort((a, b) => {
                  const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
                  const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map((site) => (
                  <option key={site.site_id || site.id} value={site.site_id || site.id}>
                  {site.site_name || site.name || 'Unknown Site'}
                  </option>
                ))}
            </select>
            <div className="flex-1 min-w-[200px]">
              <SearchBar
                placeholder="Search in products"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                iconType="search"
                className="w-full h-11 rounded-[10px] bg-white/80 border border-gray-200 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400"
                disabled={!selectedSite || isDishesLoading}
              />
            </div>
            <select
              value={selectedCategory === 'Show All' ? 'All Category' : selectedCategory}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedCategory(v === 'All Category' ? 'Show All' : v);
              }}
              className="h-11 px-4 rounded-[10px] bg-white/80 border border-gray-200 text-gray-700 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400"
              disabled={!selectedSite || isDishesLoading}
            >
              <option value="All Category">All Category</option>
              {categories.filter((c) => c !== 'Show All').map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {/* <select
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="h-12 px-4 rounded-lg bg-gray-100 border border-gray-200 text-gray-700 font-['Figtree'] text-sm outline-none focus:ring-2 focus:ring-orange-400/50"
            >
              <option>Select Brand</option>
            </select> */}
          </div>

          {/* Category tabs */}
          {/* <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-gray-100">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={combineClasses(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors font-["Figtree"]',
                  selectedCategory === cat
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                {cat}
              </button>
            ))}
          </div> */}

          {/* Product grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {sitesError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
                <p className="font-medium">Error loading sites</p>
                <p>{sitesError}</p>
              </div>
            )}
            {!selectedSite && !isSitesLoading && (
              <p className="text-center text-gray-500 py-8 font-['Figtree']">Please select a site to view products.</p>
            )}
            {selectedSite && isDishesLoading && (
              <p className="text-center text-gray-500 py-8 font-['Figtree']">Loading dishes...</p>
            )}
            {selectedSite && dishesError && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <p className="font-medium">Error loading dishes</p>
                <p>{dishesError}</p>
              </div>
            )}
            {selectedSite && !isDishesLoading && !dishesError && (
              <>
                {dishes.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600 font-['Figtree'] text-base mb-2">
                      No dishes available for this site.
                    </p>
                    <p className="text-gray-500 font-['Figtree'] text-sm">
                      Please try selecting a different site or contact support to add dishes to this site.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {filteredProducts.map((product) => (
                        <Card.Light
                          key={product.id}
                          className="relative p-4 flex flex-col min-h-[100px] border border-gray-200/80 hover:bg-white/90 transition-colors"
                        >
                          <p className="text-gray-900 font-medium font-['Figtree'] text-base mb-1 line-clamp-2 text-left">
                            {product.name}
                          </p>
                          <p className="text-red-600 font-semibold text-sm text-left">${product.price.toFixed(2)}</p>
                          <button
                            type="button"
                            onClick={() => addToOrder(product)}
                            className="absolute bottom-4 right-4 w-9 h-9 rounded-full bg-orange-500 text-white flex items-center justify-center shadow-md hover:bg-orange-600 transition-colors"
                            aria-label={`Add ${product.name}`}
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                        </Card.Light>
                      ))}
                    </div>
                    {filteredProducts.length === 0 && dishes.length > 0 && (
                      <p className="text-center text-gray-500 py-8 font-['Figtree']">No products match your filters.</p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </Card>

        {/* Right: Order summary panel (sidebar-style like PatrollingDashboard) */}
        <Card
          variant="custom"
          className={combineClasses(
            'flex flex-col overflow-hidden flex-shrink-0',
            isMobileViewport ? 'w-full flex-1 min-h-[360px]' : 'w-[400px]'
          )}
          style={panelStyle}
        >
          {/* Search and dining/table row */}
          <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-100">
            {shiftError && (
              <div className="w-full p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                <p>{shiftError}</p>
              </div>
            )}
            {isLoadingShift && (
              <div className="w-full p-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-xs">
                <p>Loading shift information...</p>
              </div>
            )}
            <select
              value={diningOption}
              onChange={(e) => setDiningOption(e.target.value)}
              className="h-11 px-4 rounded-[10px] bg-white/80 border border-gray-200 text-gray-700 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400"
              aria-label="Select dining type"
            >
              <option value="Select Dining">Select Dining</option>
              <option value="Dine-in">Dine-in</option>
              <option value="Takeaway">Takeaway</option>
              <option value="Delivery">Delivery</option>
            </select>
            {tableOptions.length > 0 ? (
              <select
                value={tableOption}
                onChange={(e) => setTableOption(e.target.value)}
                disabled={isLoadingTables}
                className="h-11 px-4 rounded-[10px] bg-white/80 border border-gray-200 text-gray-700 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400 disabled:opacity-60"
                aria-label="Select table"
              >
                <option value="Select Table">Select Table</option>
                {tableOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                min={1}
                placeholder={isLoadingTables ? 'Loading tables…' : 'Table number'}
                value={tableOption === 'Select Table' ? '' : tableOption}
                onChange={(e) => setTableOption(e.target.value.trim() || 'Select Table')}
                disabled={isLoadingTables}
                className={combineClasses(INPUT_BASE_CLASS, 'h-11')}
                aria-label="Table number"
              />
            )}
          </div>

          {/* Order header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
            <ShoppingBag className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-bold text-gray-900 font-['Figtree']">
              {orderNumber != null ? `Order #${orderNumber}` : 'New order'}
            </h2>
          </div>

          <div className="p-4">
              <SearchBar
                placeholder="Search in Existing"
                value={existingSearch}
                onChange={(e) => setExistingSearch(e.target.value)}
                iconType="search"
                className="w-full h-11 rounded-[10px] bg-white/80 border border-gray-200 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400"
              />
            </div>

          {/* Ordered items list (filtered by Search in Existing) */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {filteredOrderItems.map((item) => (
              <Card.Light
                key={item.id}
                className="p-4 flex flex-col gap-3 border border-gray-200/80 hover:bg-white/90 transition-colors"
              >
                {/* Top row: item name (left) + delete (top right) */}
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-gray-900 font-['Figtree'] text-base flex-1 min-w-0">
                    {item.name}
                  </p>
                  {/* <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="w-8 h-8 rounded-full flex items-center justify-center bg-red-100 text-red-600 hover:bg-red-200 transition-colors flex-shrink-0 border border-red-200"
                    aria-label="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button> */}

                  <div className="flex items-center rounded-lg border border-gray-200 bg-gray-100 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.id, -1)}
                      className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors"
                      aria-label="Decrease quantity"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="min-w-[32px] text-center font-medium text-gray-900 text-sm py-1">
                      {item.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.id, 1)}
                      className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors"
                      aria-label="Increase quantity"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* Middle row: price × quantity = total (total emphasized) */}
                <p className="text-sm font-['Figtree'] text-left">
                  <span className="text-amber-800">${item.price.toFixed(2)} × {item.quantity} = </span>
                  <span className="font-semibold text-amber-900">${(item.price * item.quantity).toFixed(2)}</span>
                </p>
                {/* Bottom row: Add Notes*/}
                <div className="flex items-center justify-between gap-3 flex-wrap w-full">
                  {editingNotesForItemId === item.id ? (
                    <>
                      <input
                        type="text"
                        value={item.notes || ''}
                        onChange={(e) => setItemNotes(item.id, e.target.value)}
                        placeholder="Add notes..."
                        className={`flex-1 min-w-[140px] h-9 px-3 ${INPUT_BASE_CLASS} text-sm`}
                        autoFocus
                        aria-label="Item notes"
                      />
                      <button
                        type="button"
                        onClick={() => setEditingNotesForItemId(null)}
                        className="h-8 px-3 rounded-lg bg-orange-500 text-white text-sm font-['Figtree'] hover:bg-orange-600 flex items-center gap-1.5 transition-colors shrink-0"
                      >
                        Save
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm font-['Figtree'] text-gray-800 truncate min-w-0 flex-1">
                        {item.notes || <span className="text-gray-500 italic">No notes</span>}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditingNotesForItemId(item.id)}
                        className="h-8 px-3 rounded-lg bg-gray-100 border border-gray-300 text-gray-800 text-sm font-['Figtree'] hover:bg-gray-200 flex items-center gap-1.5 transition-colors shrink-0"
                      >
                        <FileEdit className="w-4 h-4 text-gray-700" />
                        {item.notes ? 'Edit Notes' : 'Add Notes'}
                      </button>
                    </>
                  )}
                </div>
              </Card.Light>
            ))}
            {filteredOrderItems.length === 0 && (
              <p className="text-center text-gray-500 py-6 font-['Figtree'] text-sm">
                {orderItems.length === 0
                  ? 'No items in this order.'
                  : `No items match "${existingSearch}".`}
              </p>
            )}
          </div>

          {/* Order summary totals */}
          {/* <div className="border-t border-gray-200 p-4 space-y-2 bg-gray-50/50 rounded-b-xl">
            <div className="flex justify-between text-sm text-gray-700 font-['Figtree']">
              <span>Sub total:</span>
              <span>{subTotal}$</span>
            </div>
            <div className="flex justify-between text-sm text-gray-700 font-['Figtree']">
              <span>Product Discount:</span>
              <span>{productDiscount}$</span>
            </div>
            <div className="flex justify-between text-sm text-gray-700 font-['Figtree'] items-center gap-2">
              <span>Extra Discount:</span>
              <span className="flex items-center gap-1">
                {extraDiscount}$
                <button
                  type="button"
                  className="p-1 rounded text-gray-500 hover:bg-gray-200"
                  aria-label="Edit extra discount"
                >
                  <FileEdit className="w-4 h-4" />
                </button>
              </span>
            </div>
            <div className="flex justify-between text-sm text-gray-700 font-['Figtree'] items-center gap-2">
              <span>Coupon discount:</span>
              <span className="flex items-center gap-1">
                {couponDiscount}$
                <button
                  type="button"
                  className="p-1 rounded text-gray-500 hover:bg-gray-200"
                  aria-label="Edit coupon"
                >
                  <FileEdit className="w-4 h-4" />
                </button>
              </span>
            </div>
            <div className="flex justify-between text-base font-bold text-gray-900 font-['Figtree'] pt-2 border-t border-gray-200">
              <span>Total:</span>
              <span>{Math.max(0, total).toFixed(0)}$</span>
            </div>
          </div> */}

          {/* Order summary totals */}
          <div className="border-t border-gray-200 p-4 space-y-2 bg-gray-50/50 rounded-b-xl">
            <div className="flex justify-between text-sm text-gray-700 font-['Figtree']">
              <span>Sub total:</span>
              <span>${subTotal.toFixed(2)}</span>
            </div>
            {placeOrderError && (
              <div className="p-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs">
                <p>{placeOrderError}</p>
              </div>
            )}
            <button
              type="button"
              onClick={handlePlaceOrder}
              disabled={isPlacingOrder || !selectedSite || !ssId || orderItems.length === 0}
              className="w-full mt-4 py-3 px-4 rounded-xl bg-orange-500 text-white font-semibold text-sm font-['Figtree'] hover:bg-orange-600 active:bg-orange-700 transition-colors shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isPlacingOrder ? 'Placing Order...' : 'Confirm Order'}
            </button>
          </div>
        </Card>
      </div>

      <SuccessOverlay
        open={showSuccessOverlay}
        onClose={() => { setShowSuccessOverlay(false); setLastCreatedOrderId(null); }}
        title="Order Placed Successfully"
        message={lastCreatedOrderId ? `Order ID: ${lastCreatedOrderId}` : undefined}
        buttonLabel="Done"
      />
    </div>
  );
};

export default PlaceOrderScreen;
